import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { accessOptions } from '@/features/auth/access';
import { apiRequest, buildUrl } from '@/lib/http/http-api';
import type {
  CreateEnterpriseInput,
  EnterpriseAccount,
  EnterpriseDetail,
  Enterprise,
  EnterprisePage,
  EnterpriseQuery,
  EnterpriseStatusInput,
  EnterpriseVerificationInput,
  UpdateEnterpriseInput,
} from './enterprise-types';

export const enterpriseLogoUrl = (url: string, fileId?: string | null) => {
  if (fileId) return buildUrl(`/api/admin/enterprises/logo/${fileId}/content`);
  const path = url.replace(
    '/api/merchant/enterprises/logo/',
    '/api/admin/enterprises/logo/',
  );
  return path.startsWith('/api/') ? buildUrl(path) : path;
};
export const uploadEnterpriseLogo = (file: File, signal: AbortSignal) => {
  const form = new FormData();
  form.append('file', file);
  return apiRequest<{ fileId: string; url: string; originalName: string }>(
    '/api/admin/enterprises/logo',
    { method: 'POST', form, signal, timeoutMs: 60_000 },
  );
};

const enterprisesKey = ['enterprises'] as const;
const detailKey = (id: string) => [...enterprisesKey, 'detail', id] as const;
const enterprisePath = (id: string) =>
  `/api/admin/enterprises/${encodeURIComponent(id)}`;

export const fetchEnterpriseReview = (id: string, signal?: AbortSignal) =>
  apiRequest<EnterpriseDetail>(enterprisePath(id), { signal });

export const enterpriseEvidenceUrl = (
  id: string,
  documentId: string,
  download = false,
) =>
  buildUrl(
    `${enterprisePath(id)}/evidence/${documentId}/content`,
    download ? { download: 1 } : undefined,
  );

export const requireEnterpriseAdmin = async (
  client: QueryClient,
  href: string,
) => {
  if (!(await client.fetchQuery(accessOptions)).isAdmin) {
    throw redirect({ to: '/forbidden', search: { from: href } });
  }
};

export const useEnterprises = (query: EnterpriseQuery) =>
  useQuery({
    queryKey: [...enterprisesKey, 'list', query],
    queryFn: ({ signal }) =>
      apiRequest<EnterprisePage>('/api/admin/enterprises', {
        search: query,
        signal,
      }),
    placeholderData: keepPreviousData,
  });

export const exportEnterprises = (
  query: Omit<EnterpriseQuery, 'page' | 'pageSize'>,
  signal: AbortSignal,
) =>
  apiRequest<Enterprise[]>('/api/admin/enterprises/export', {
    search: query,
    signal,
    timeoutMs: 300_000,
  });

export const enterpriseQueryOptions = (id: string) =>
  queryOptions({
    queryKey: detailKey(id),
    queryFn: ({ signal }) =>
      apiRequest<EnterpriseDetail>(enterprisePath(id), { signal }),
  });
export const useEnterprise = (id: string) =>
  useQuery(enterpriseQueryOptions(id));

export const searchEnterpriseAccounts = (keyword?: string) =>
  apiRequest<EnterpriseAccount[]>('/api/admin/enterprises/account-options', {
    search: { keyword },
  });

const useEnterpriseMutation = <Input>(
  request: (input: Input) => Promise<EnterpriseDetail>,
) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (enterprise) => {
      client.setQueryData(detailKey(enterprise.id), enterprise);
      void client.invalidateQueries({
        queryKey: ['operations-workbench', 'overview'],
      });
      void client.invalidateQueries({ queryKey: enterprisesKey });
      void client.invalidateQueries({ queryKey: ['opportunities'] });
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: enterprisesKey });
    },
  });
};

export const useCreateEnterprise = () =>
  useEnterpriseMutation((input: CreateEnterpriseInput) =>
    apiRequest<EnterpriseDetail>('/api/admin/enterprises', {
      method: 'POST',
      body: input,
    }),
  );
export const useUpdateEnterprise = () =>
  useEnterpriseMutation(
    ({ id, input }: { id: string; input: UpdateEnterpriseInput }) =>
      apiRequest<EnterpriseDetail>(enterprisePath(id), {
        method: 'PATCH',
        body: input,
      }),
  );
export const useEnterpriseStatus = () =>
  useEnterpriseMutation(
    ({ id, input }: { id: string; input: EnterpriseStatusInput }) =>
      apiRequest<EnterpriseDetail>(`${enterprisePath(id)}/status`, {
        method: 'POST',
        body: input,
      }),
  );
export const useEnterpriseVerification = () =>
  useEnterpriseMutation(
    ({ id, input }: { id: string; input: EnterpriseVerificationInput }) =>
      apiRequest<EnterpriseDetail>(`${enterprisePath(id)}/verification`, {
        method: 'POST',
        body: input,
      }),
  );
